"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";

export interface FavoriteItem {
  id: string;
  name: string;
  category: string;
  meaning?: string | null;
  timestamp: string;
}

interface FavoritesContextType {
  favorites: FavoriteItem[];
  addFavorite: (name: string, category: string, meaning?: string | null) => void;
  removeFavorite: (id: string) => void;
  isFavorite: (name: string) => boolean;
  clearFavorites: () => void;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const FavoritesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("fantasy_name_favorites");
      if (stored) {
        setFavorites(JSON.parse(stored));
      }
    } catch (e) {
      console.error("Failed to load favorites", e);
    }
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    try {
      localStorage.setItem("fantasy_name_favorites", JSON.stringify(favorites));
    } catch (e) {
      console.error("Failed to save favorites", e);
    }
  }, [favorites, mounted]);

  const addFavorite = useCallback(
    (name: string, category: string, meaning?: string | null) => {
      const newItem: FavoriteItem = {
        id: `${name}-${category}-${Date.now()}`,
        name,
        category: category || "General",
        meaning: meaning || null,
        timestamp: new Date().toISOString(),
      };
      setFavorites((prev) => [newItem, ...prev.filter((f) => f.name !== name)]);
    },
    []
  );

  const removeFavorite = useCallback((idOrName: string) => {
    setFavorites((prev) =>
      prev.filter((item) => item.id !== idOrName && item.name !== idOrName)
    );
  }, []);

  const favoriteNames = useMemo(
    () => new Set(favorites.map((item) => item.name)),
    [favorites]
  );

  const isFavorite = useCallback(
    (name: string) => favoriteNames.has(name),
    [favoriteNames]
  );

  const clearFavorites = useCallback(() => {
    setFavorites([]);
  }, []);

  // Memoized so the thousands of NameCard consumers do not all re-render on
  // every unrelated render of this provider.
  const value = useMemo(
    () => ({
      favorites,
      addFavorite,
      removeFavorite,
      isFavorite,
      clearFavorites,
    }),
    [favorites, addFavorite, removeFavorite, isFavorite, clearFavorites]
  );

  return (
    <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error("useFavorites must be used within a FavoritesProvider");
  }
  return context;
};
